/**
 * Apple TV → Locations. A direct port of the corporate dashboard's
 * appletv-locations-tree: the same nested tree, the same row actions, the same
 * dialog. Only the styling is Prospera's.
 *
 * Rows nest: children render indented beneath their parent, and a parent row
 * click expands or collapses it. Sub-locations are created from the parent's
 * row. Searching drops to a flat list of matches, since a hit three levels down
 * is invisible in a collapsed tree.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ImageOff, MapPin, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import {
  createLocation,
  deleteLocation,
  getLocationImageUploadUrl,
  listLocations,
  updateLocation,
  uploadToStorage,
  type AppleTvLocation,
} from '@/services/appleTv.service';
import { AppleTvGate, AppleTvPageShell, shouldGate, useAppleTvAccess } from './useAppleTvAccess';
import { IconAction, Table } from './AppleTvTable';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];

interface LocationForm {
  name: string;
  address: string;
  description: string;
  phoneNumber: string;
  parentLocationId: number | null;
  imageStorageKey: string | null;
  imageUrl: string | null;
}

const emptyForm: LocationForm = {
  name: '',
  address: '',
  description: '',
  phoneNumber: '',
  parentLocationId: null,
  imageStorageKey: null,
  imageUrl: null,
};

export default function AppleTvLocationsPage() {
  const access = useAppleTvAccess();
  const { configured, canManage } = access;

  const [locations, setLocations] = useState<AppleTvLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AppleTvLocation | null>(null);
  const [form, setForm] = useState<LocationForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<AppleTvLocation | null>(null);

  useEffect(() => {
    if (!configured) return;
    setLoading(true);
    listLocations()
      .then((data) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load locations'))
      .finally(() => setLoading(false));
  }, [configured]);

  const byId = useMemo(() => {
    const map = new Map<number, AppleTvLocation>();
    for (const l of locations) map.set(l.id, l);
    return map;
  }, [locations]);

  // Children keyed by parent id; orphans (parent unreachable) treated as roots.
  const childrenOf = useMemo(() => {
    const map = new Map<number | null, AppleTvLocation[]>();
    for (const l of locations) {
      const key = l.parentLocationId != null && byId.has(l.parentLocationId) ? l.parentLocationId : null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(l);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.name.localeCompare(b.name));
    return map;
  }, [locations, byId]);

  // Descendant set for a node (to exclude from the parent picker on edit).
  const descendantIds = useCallback(
    (rootId: number): Set<number> => {
      const out = new Set<number>();
      const stack = [rootId];
      while (stack.length) {
        const id = stack.pop()!;
        for (const child of childrenOf.get(id) ?? []) {
          if (!out.has(child.id)) {
            out.add(child.id);
            stack.push(child.id);
          }
        }
      }
      return out;
    },
    [childrenOf],
  );

  const search = searchQuery.trim().toLowerCase();
  const filteredFlat = useMemo(() => {
    if (!search) return null;
    return locations
      .filter(
        (l) =>
          l.name.toLowerCase().includes(search) ||
          l.address.toLowerCase().includes(search) ||
          (l.description ?? '').toLowerCase().includes(search),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [locations, search]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const parentOptions = useMemo(() => {
    const excluded = new Set<number>();
    if (editing) {
      excluded.add(editing.id);
      for (const d of descendantIds(editing.id)) excluded.add(d);
    }
    return locations
      .filter((l) => !excluded.has(l.id))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({ value: l.id, label: l.name }));
  }, [locations, editing, descendantIds]);

  const openCreate = (parent?: AppleTvLocation) => {
    setEditing(null);
    setForm({ ...emptyForm, parentLocationId: parent ? parent.id : null });
    setDialogOpen(true);
  };

  const openEdit = (loc: AppleTvLocation) => {
    setEditing(loc);
    setForm({
      name: loc.name,
      address: loc.address,
      description: loc.description ?? '',
      phoneNumber: loc.phoneNumber ?? '',
      parentLocationId: loc.parentLocationId,
      imageStorageKey: loc.imageStorageKey,
      imageUrl: loc.imageUrl,
    });
    setDialogOpen(true);
  };

  const handleImageSelect = async (file: File | undefined) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Unsupported image. Use JPEG, PNG, GIF, WebP, or HEIC.');
      return;
    }
    const preview = URL.createObjectURL(file);
    setForm((f) => ({ ...f, imageUrl: preview }));
    setUploadingImage(true);
    try {
      const { uploadUrl, storageKey, contentType } = await getLocationImageUploadUrl({
        contentType: file.type,
        fileName: file.name,
      });
      await uploadToStorage(uploadUrl, contentType, file);
      setForm((f) => ({ ...f, imageStorageKey: storageKey }));
    } catch {
      setForm((f) => ({
        ...f,
        imageUrl: editing?.imageUrl ?? null,
        imageStorageKey: editing?.imageStorageKey ?? null,
      }));
      toast.error('Image upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const clearImage = () => setForm((f) => ({ ...f, imageUrl: null, imageStorageKey: null }));

  const errors = { name: !form.name.trim(), address: !form.address.trim() };
  const hasErrors = errors.name || errors.address;

  const handleSave = async () => {
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        description: form.description.trim() || null,
        phoneNumber: form.phoneNumber.trim() || null,
        imageStorageKey: form.imageStorageKey,
        parentLocationId: form.parentLocationId,
      };
      if (editing) {
        const updated = await updateLocation(editing.id, payload);
        setLocations((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
        toast.success('Location updated');
      } else {
        const created = await createLocation(payload);
        setLocations((prev) => [...prev, created]);
        if (payload.parentLocationId != null) {
          setExpanded((prev) => new Set(prev).add(payload.parentLocationId!));
        }
        toast.success('Location created');
      }
      setDialogOpen(false);
    } catch {
      toast.error('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLocation(deleteTarget.id);
      setLocations((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success('Location deleted');
    } catch {
      toast.error('Cannot delete — remove its child locations, screens and groups first.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const renderRow = (loc: AppleTvLocation, depth: number): React.ReactNode => {
    const kids = childrenOf.get(loc.id) ?? [];
    const isOpen = expanded.has(loc.id);
    // A parent row click expands it; a leaf row click opens it for editing.
    const onRowClick = kids.length > 0 ? () => toggle(loc.id) : canManage ? () => openEdit(loc) : undefined;
    return (
      <React.Fragment key={loc.id}>
        <Table.Row onClick={onRowClick}>
          <Table.Cell>
            <div style={{ paddingLeft: depth * 20 }} className="flex items-center gap-2">
              {kids.length > 0 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(loc.id);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                  aria-label={isOpen ? 'Collapse' : 'Expand'}
                >
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              ) : (
                <span className="inline-block w-4" />
              )}
              {loc.imageUrl ? (
                <img src={loc.imageUrl} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-400">
                  <MapPin className="h-4 w-4" />
                </span>
              )}
              <span className="font-medium text-slate-900">{loc.name}</span>
              {kids.length > 0 && (
                <span className="text-xs text-slate-400">({kids.length})</span>
              )}
            </div>
          </Table.Cell>
          <Table.Cell truncate>{loc.address}</Table.Cell>
          <Table.Cell truncate>{loc.phoneNumber ?? '—'}</Table.Cell>
          <Table.Cell actions>
            {canManage && (
              <>
                <IconAction title="Add sub-location" tone="primary" onClick={() => openCreate(loc)}>
                  <Plus className="h-4 w-4" />
                </IconAction>
                <IconAction title="Edit" tone="primary" onClick={() => openEdit(loc)}>
                  <Pencil className="h-4 w-4" />
                </IconAction>
                <IconAction title="Delete" tone="danger" onClick={() => setDeleteTarget(loc)}>
                  <Trash2 className="h-4 w-4" />
                </IconAction>
              </>
            )}
          </Table.Cell>
        </Table.Row>
        {isOpen && kids.map((child) => renderRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  const roots = childrenOf.get(null) ?? [];
  const isEmpty = !loading && locations.length === 0;
  const gated = shouldGate(access);

  return (
    <AppleTvPageShell>
      <div className="shrink-0 p-6 pb-0">
        <PageHeader title="Locations" subtitle="The sites your Apple TV screens are installed in." />
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-6">
        {gated ? (
          <AppleTvGate {...access} />
        ) : (
          <Table.Root>
            <Table.Toolbar>
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search locations by name, address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {canManage && (
                <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openCreate()}>
                  Add Location
                </Button>
              )}
            </Table.Toolbar>

            {loading ? (
              <Table.Loading />
            ) : isEmpty ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
                <MapPin className="h-10 w-10 text-slate-300" />
                <div>
                  <p className="font-medium text-slate-900">No locations yet</p>
                  <p className="text-sm text-slate-500">
                    Create your first location — screens and groups live under one.
                  </p>
                </div>
                {canManage && (
                  <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openCreate()}>
                    Create your first location
                  </Button>
                )}
              </div>
            ) : (
              <Table.Content>
                <table className="w-full table-fixed">
                  <Table.Header>
                    <tr>
                      <Table.Th className="w-[40%]">Name</Table.Th>
                      <Table.Th>Address</Table.Th>
                      <Table.Th className="w-40">Phone</Table.Th>
                      <Table.Th className="w-32 text-right">Actions</Table.Th>
                    </tr>
                  </Table.Header>
                  <tbody className="divide-y divide-slate-200">
                    {filteredFlat ? (
                      filteredFlat.length === 0 ? (
                        <Table.Empty colSpan={4} message="No locations match your search" />
                      ) : (
                        filteredFlat.map((loc) => renderRow(loc, 0))
                      )
                    ) : (
                      roots.map((loc) => renderRow(loc, 0))
                    )}
                  </tbody>
                </table>
              </Table.Content>
            )}
          </Table.Root>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? 'Edit Location' : 'Add Location'}
        closeOnEscape={false}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={uploadingImage || hasErrors}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        }
      >
        <div className="grid gap-4">
          <FormField label="Name" required error={errors.name && form.name !== '' ? undefined : undefined}>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Enter location name"
            />
          </FormField>
          <FormField label="Address" required>
            <Textarea
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Street address"
              rows={2}
            />
          </FormField>
          <FormField label="Description">
            <Textarea autoResize
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional notes"
              rows={2}
            />
          </FormField>
          <FormField label="Phone">
            <Input
              value={form.phoneNumber}
              onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
              placeholder="Optional phone number"
            />
          </FormField>
          <FormField label="Parent location">
            <Select
              options={parentOptions}
              value={form.parentLocationId}
              onChange={(v) => setForm((f) => ({ ...f, parentLocationId: v }))}
              placeholder="None (top level)"
              clearable
              searchable
            />
          </FormField>
          <FormField label="Image">
            <div className="flex items-center gap-3">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {uploadingImage ? (
                  <Spinner size="sm" label="" />
                ) : form.imageUrl ? (
                  <img src={form.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <ImageOff className="h-6 w-6 text-slate-300" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex cursor-pointer items-center rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
                  {form.imageUrl ? 'Replace image' : 'Upload image'}
                  <input
                    type="file"
                    accept={ALLOWED_IMAGE_TYPES.join(',')}
                    className="hidden"
                    onChange={(e) => handleImageSelect(e.target.files?.[0])}
                  />
                </label>
                {form.imageUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    leftIcon={<X className="h-3.5 w-3.5" />}
                    className="justify-start text-slate-500 hover:text-red-600"
                    onClick={clearImage}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </FormField>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete “${deleteTarget?.name}”?`}
        message="A location can only be deleted once it has no child locations, screens, or groups. Its image is removed too."
        confirmLabel="Delete"
      />
    </AppleTvPageShell>
  );
}
